import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const API = process.env.REACT_APP_API_URL;

/**
 * One dialog for both "Add Service" and "Edit Service".
 *
 * Props
 *  - token                 admin token
 *  - service               service object to edit (omit / null to create)
 *  - defaultCategoryId     preselected category when creating
 *  - defaultSubcategoryId  preselected subcategory when creating
 *  - lockCategory          when creating from a category screen: the Category field is hidden and
 *                          defaultCategoryId is sent automatically (always hidden when editing)
 *  - lockSubcategory       when creating from inside a subcategory: the Subcategory field is hidden and
 *                          defaultSubcategoryId is sent automatically
 *  - onClose()             close without saving
 *  - onSaved()             called after a successful save (caller closes + reloads)
 */
const ServiceFormDialog = ({
  token,
  service,
  defaultCategoryId,
  defaultSubcategoryId,
  lockCategory = false,
  lockSubcategory = false,
  onClose,
  onSaved,
}) => {
  const isEdit = !!service;
  // Category field is hidden when editing, and when adding from a category screen.
  // The value still comes from state and is sent to the backend.
  const categoryHidden = isEdit || (lockCategory && !!defaultCategoryId);
  // Subcategory field is hidden when adding from inside a subcategory.
  const subcategoryHidden =
    !isEdit && lockSubcategory && !!defaultSubcategoryId;
  const originalCategoryId =
    service?.category?.id != null ? String(service.category.id) : "";
  const originalSubcategoryId =
    service?.subcategory?.id != null ? String(service.subcategory.id) : "";

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  // const [handledByOptions, setHandledByOptions] = useState([]);
  // const [handledByIds, setHandledByIds] = useState(
  //   (service?.match_subcategory_ids || []).map(String),
  // );
  const [categoryId, setCategoryId] = useState(
    isEdit ? originalCategoryId : String(defaultCategoryId || ""),
  );
  const [subcategoryId, setSubcategoryId] = useState(
    isEdit
      ? originalSubcategoryId
      : defaultSubcategoryId
        ? String(defaultSubcategoryId)
        : "",
  );
  // const DIAGNOSIS_CATEGORY_ID = "4";
  // const STANDARD_CATEGORY_ID = "3";

  // const isDiagnosisCategory = categoryId === DIAGNOSIS_CATEGORY_ID;
  // const [handledByOpen, setHandledByOpen] = useState(false);

  // On update, subcategory_id is only sent when the user changed it.
  // Omitting it keeps the current subcategory; sending it empty removes it.
  const [subcategoryTouched, setSubcategoryTouched] = useState(false);
  const [name, setName] = useState(service?.name || "");
  const [nameAr, setNameAr] = useState(service?.name_ar || "");
  const [description, setDescription] = useState(service?.description || "");
  const [descriptionAr, setDescriptionAr] = useState(
    service?.description_ar || "",
  );
  const [durationMinutes, setDurationMinutes] = useState(
    service?.durationMinutes != null ? String(service.durationMinutes) : "",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (categoryHidden) return;
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${API}api/admin/service-categories?limit=100`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (response.ok) setCategories(json.data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [token, categoryHidden]);

  const fetchSubcategories = useCallback(
    async (catId) => {
      if (!catId) {
        setSubcategories([]);
        return [];
      }
      try {
        const response = await fetch(
          `${API}api/admin/service-subcategories?category_id=${catId}&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        const list = response.ok ? json.data.subcategories || [] : [];
        setSubcategories(list);
        return list;
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        return [];
      }
    },
    [token],
  );

  // useEffect(() => {
  //   if (subcategoryHidden) return;
  //   fetchSubcategories(categoryId);
  // }, [categoryId, subcategoryHidden, fetchSubcategories]);

  // useEffect(() => {
  //   if (!isDiagnosisCategory) {
  //     setHandledByOptions([]);
  //     return;
  //   }

  //   const fetchHandledByOptions = async () => {
  //     try {
  //       const response = await fetch(
  //         `${API}api/admin/service-subcategories?category_id=${STANDARD_CATEGORY_ID}&limit=100`,
  //         {
  //           headers: { Authorization: `Bearer ${token}` },
  //         },
  //       );

  //       const json = await response.json();

  //       if (response.ok) {
  //         setHandledByOptions(json.data.subcategories || []);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching Handled By subcategories:", error);
  //     }
  //   };

  //   fetchHandledByOptions();
  // }, [isDiagnosisCategory, token]);

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    if (isEdit && value === originalCategoryId) {
      // back to the original category: restore its subcategory
      setSubcategoryId(originalSubcategoryId);
      setSubcategoryTouched(false);
    } else {
      // add subcategory belongs to one category, so reset it
      setSubcategoryId("");
      setSubcategoryTouched(true);
    }
  };

  const handleSubcategoryChange = (value) => {
    setSubcategoryId(value);
    setSubcategoryTouched(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId) return toast.error("Category is required");
    if (!name.trim()) return toast.error("Service name is required");

    const formData = new FormData();
    formData.append("category_id", categoryId);
    if (isEdit) {
      if (subcategoryTouched) formData.append("subcategory_id", subcategoryId);
    } else if (subcategoryId) {
      formData.append("subcategory_id", subcategoryId);
    }
    formData.append("name", name.trim());
    formData.append("name_ar", nameAr.trim());
    if (isEdit || description.trim()) {
      formData.append("description", description.trim()); // empty clears on update
    }
    if (isEdit || descriptionAr.trim()) {
      formData.append("description_ar", descriptionAr.trim());
    }
    if (durationMinutes) formData.append("duration_minutes", durationMinutes);
    formData.append("name", name.trim());

    // if (isDiagnosisCategory) {
    //   formData.append(
    //     "match_subcategory_ids",
    //     JSON.stringify(handledByIds.map(Number)),
    //   );
    // }
    setSaving(true);
    try {
      const response = await fetch(
        isEdit
          ? `${API}api/admin/services/${service.id}`
          : `${API}api/admin/services`,
        {
          method: isEdit ? "PUT" : "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await response.json();
      if (response.ok) {
        toast.success(
          isEdit
            ? "Service updated successfully"
            : "Service created successfully",
        );
        onSaved();
      } else {
        // backend validation messages are shown as-is
        toast.error(
          json.errorMessage ||
            (isEdit ? "Failed to update service" : "Failed to create service"),
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while saving the service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-form-title"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "700px", width: "95%" }}
        >
          <div
            className="modal-content"
            style={{
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <form
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                flex: "1 1 auto",
                overflow: "hidden",
              }}
              onSubmit={handleSubmit}
            >
              <div className="modal-header">
                <h5 className="fs-5 modal-title" id="service-form-title">
                  {isEdit ? "Edit Service" : "Add Service"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                  aria-label="Close"
                />
              </div>

              <div
                className="modal-body"
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {!categoryHidden && (
                  <div className="mb-3">
                    <label htmlFor="service-category" className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      id="service-category"
                      className="form-select"
                      value={categoryId}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">Select category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {categoryId && !subcategoryHidden && (
                  <div className="mb-3">
                    <label htmlFor="service-subcategory" className="form-label">
                      Subcategory (optional)
                    </label>
                    <div style={{ position: "relative" }}>
                      <select
                        id="service-subcategory"
                        className="form-select"
                        value={subcategoryId}
                        onChange={(e) =>
                          handleSubcategoryChange(e.target.value)
                        }
                        style={{
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          backgroundImage: "none",
                          paddingRight: "40px",
                        }}
                      >
                        <option value="">
                          No subcategory (directly under category)
                        </option>

                        {subcategories.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>

                      <Icon
                        icon="solar:alt-arrow-down-linear"
                        style={{
                          position: "absolute",
                          right: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* {isDiagnosisCategory && (
                  <div className="mb-3">
                    <label className="form-label">Handled By</label>

                    <div className="position-relative">
                      <button
                        type="button"
                        className="form-control text-start d-flex align-items-center justify-content-between"
                        onClick={() => setHandledByOpen((prev) => !prev)}
                        aria-expanded={handledByOpen}
                        style={{ overflow: "hidden", gap: "8px" }}
                        title={handledByIds
                          .map(
                            (id) =>
                              handledByOptions.find(
                                (option) => String(option.id) === id,
                              )?.name,
                          )
                          .filter(Boolean)
                          .join(", ")}
                      >
                        <span
                          className="d-flex align-items-center gap-1"
                          style={{
                            minWidth: 0,
                            flex: 1,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {handledByIds.length > 0 ? (
                            handledByIds.map((id) => {
                              const item = handledByOptions.find(
                                (option) => String(option.id) === id,
                              );

                              return item ? (
                                <span
                                  key={id}
                                  className="badge bg-primary"
                                  style={{
                                    flexShrink: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.name}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-muted text-truncate">
                              Select standard subcategories
                            </span>
                          )}
                        </span>

                        <span style={{ flexShrink: 0 }}>
                          <Icon icon="solar:alt-arrow-down-linear" />
                        </span>
                      </button>

                      {handledByOpen && (
                        <div
                          className="border rounded bg-white mt-1 p-2"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            maxHeight: "220px",
                            overflowY: "auto",
                          }}
                        >
                          {handledByOptions.map((item) => {
                            const id = String(item.id);
                            const checked = handledByIds.includes(id);

                            return (
                              <label
                                key={item.id}
                                className="d-flex align-items-center gap-2 p-2 mb-0"
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0"
                                  checked={checked}
                                  onChange={(e) => {
                                    setHandledByIds((prev) =>
                                      e.target.checked
                                        ? [...prev, id]
                                        : prev.filter(
                                            (selectedId) => selectedId !== id,
                                          ),
                                    );
                                  }}
                                />
                                <span>{item.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )} */}

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="service-name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="service-name"
                      type="text"
                      className="form-control"
                      placeholder="e.g. Wheel Alignment"
                      dir="ltr"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label
                      htmlFor="service-name-ar"
                      className="form-label"
                      style={{
                        textAlign: "right",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      اسم الخدمة
                    </label>
                    <input
                      id="service-name-ar"
                      type="text"
                      className="form-control"
                      placeholder="أدخل اسم الخدمة"
                      dir="rtl"
                      value={nameAr}
                      onChange={(e) => setNameAr(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="service-duration" className="form-label">
                    Duration (minutes)
                  </label>
                  <input
                    id="service-duration"
                    type="number"
                    min="1"
                    className="form-control"
                    placeholder="e.g. 60"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="service-description" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="service-description"
                    className="form-control"
                    rows="3"
                    placeholder="e.g. Coolant, Brake, Gearbox"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div className="mb-0">
                  <label
                    htmlFor="service-description-ar"
                    className="form-label w-100 text-end"
                  >
                    الوصف
                  </label>
                  <textarea
                    id="service-description-ar"
                    className="form-control"
                    rows="3"
                    placeholder="أدخل الوصف"
                    dir="rtl"
                    value={descriptionAr}
                    onChange={(e) => setDescriptionAr(e.target.value)}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary-600"
                  disabled={saving}
                >
                  {isEdit ? "Save Changes" : "Add Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceFormDialog;
